import { createSignal, type JSX, Show } from "solid-js";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Divider,
  Stack,
  TextField,
} from "@/components/ui";
import { sendMagicLink, signInWithGoogle } from "@/state/auth";
import { config } from "@/state/me";

/** ログイン後は元の画面に戻す。`/device?user_code=...` のようにクエリまで含めて戻す。 */
const currentPath = () => `${window.location.pathname}${window.location.search}`;

/**
 * ログイン。
 *
 * - ブラウザで認可: Google (credentials が設定されているときだけ出す)
 * - メールで認可: 届いたリンクを踏むとログイン (パスワードは持たない)
 */
export function LoginDialog(props: { onClose: () => void }): JSX.Element {
  const [email, setEmail] = createSignal("");
  const [sent, setSent] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [sending, setSending] = createSignal(false);

  const submitMagicLink = async () => {
    const address = email().trim();
    if (address === "" || sending()) return;
    setSending(true);
    setError(null);
    try {
      await sendMagicLink(address, currentPath());
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "メールを送れませんでした");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={props.onClose} size="sm">
      <DialogHeader>ログイン</DialogHeader>
      <DialogBody>
        <Stack gap={3}>
          <p class="page-lead">
            ログインすると、猫と名前がどの端末でも同じになります。ログインしなくても、
            書き込みが許可されたスレッドには投稿できます。
          </p>

          <Show when={error()}>{(message) => <Alert variant="danger">{message()}</Alert>}</Show>

          <Show when={config()?.auth.google === true}>
            <Button variant="neutral" onClick={() => void signInWithGoogle(currentPath())}>
              Google でログイン
            </Button>
            <Divider />
          </Show>

          <Show
            when={!sent()}
            fallback={
              <Alert variant="success">
                {email()} にリンクを送りました。メールを開いてリンクを踏んでください (5 分間有効)。
              </Alert>
            }
          >
            <Stack gap={2}>
              <TextField
                label="メールアドレス"
                type="email"
                value={email()}
                onInput={setEmail}
                placeholder="you@example.com"
                hint="パスワードは使いません。届いたリンクでログインします。"
              />
              <Button
                variant="primary"
                loading={sending()}
                disabled={email().trim() === ""}
                onClick={submitMagicLink}
              >
                リンクを送る
              </Button>
            </Stack>
          </Show>
        </Stack>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={props.onClose}>
          閉じる
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
